import hash from 'hash.js';
import type { CurrentUser } from '../api/active/generated/types';
import type { CpqState, Quote, StorageLike } from '../pages/cpq-v2/state/types';
import { createCatalogSessionScopeKey } from './catalogSessionResource';
import { isFormalNewQuoteId } from './quoteLegacyProjection';
import { bindKnownQuoteVersion } from './quoteWorkspaceRoute';

export const FORMAL_WORKSPACE_SESSION_KEY =
  'quotation-system:formal-workspace-session:v1';

/** One-time safety boundary for snapshots created before catalog cost projection. */
export const FORMAL_WORKSPACE_SNAPSHOT_POLICY = 'catalog-cost-v1';

export type FormalWorkspaceSessionScope = {
  companyId: string;
  actorUserId: string;
  authorizationKey: string;
};

export function createFormalWorkspaceSessionScope(
  user: CurrentUser,
): FormalWorkspaceSessionScope {
  return {
    companyId: user.companyId,
    actorUserId: user.id,
    authorizationKey: JSON.stringify({
      snapshotPolicy: FORMAL_WORKSPACE_SNAPSHOT_POLICY,
      catalogScope: createCatalogSessionScopeKey({
        actorUserId: user.id,
        companyId: user.companyId,
        permissionVersion: user.permissionVersion,
        roleViewId: user.roleView?.roleId,
        roleViewPolicyVersion: user.roleView?.policyVersion,
        permissionDataScopes: user.permissionDataScopes,
        permissions: user.permissions,
        dataScopes: user.dataScopes,
        departments: user.departments,
        primaryDepartmentId: user.primaryDepartmentId,
      }),
      roles: [...new Set(user.roles)].sort(),
    }),
  };
}

function authorizationFingerprint(scope: FormalWorkspaceSessionScope): string {
  return hash.sha256().update(scope.authorizationKey).digest('hex');
}

export function formalWorkspaceSessionStorageKey(
  scope: FormalWorkspaceSessionScope,
): string {
  const fingerprint = hash
    .sha256()
    .update(
      JSON.stringify({
        companyId: scope.companyId,
        actorUserId: scope.actorUserId,
        authorizationKey: scope.authorizationKey,
      }),
    )
    .digest('hex');
  return `quotation-system:formal-workspace-session:v3:${fingerprint}`;
}

/** Read-only compatibility; never write or remove an old role's private draft. */
export function legacyFormalWorkspaceSessionStorageKey(
  scope: FormalWorkspaceSessionScope,
): string {
  return `quotation-system:formal-workspace-session:v2:${encodeURIComponent(JSON.stringify(scope))}`;
}

export type WorkspaceTabs = {
  actorUserId: string;
  activeQuoteId: string;
  activeQuoteVersionId?: string;
  openQuoteIds: string[];
  quotes: Record<string, Quote>;
};

export type FormalWorkspaceSessionInputs = {
  companyId: string;
  actorUserId: string;
  authorizationKey: string;
  activeQuoteId: string;
  activeQuoteVersionId?: string;
  openQuoteIds: string[];
  openQuotes: Array<Quote | undefined>;
};

/** Cheap immutable-state selector for the fields that can affect a snapshot. */
export function formalWorkspaceSessionInputs(
  state: CpqState,
  scope: FormalWorkspaceSessionScope,
): FormalWorkspaceSessionInputs {
  const openQuoteIds = state.openQuoteIds.filter(isFormalQuoteId).slice(0, 10);
  return {
    companyId: scope.companyId,
    actorUserId: scope.actorUserId,
    authorizationKey: scope.authorizationKey,
    activeQuoteId: state.activeQuoteId,
    activeQuoteVersionId: state.activeQuoteVersionId,
    openQuoteIds,
    openQuotes: openQuoteIds.map((id) => state.quotes[id]),
  };
}

export function sameFormalWorkspaceSessionInputs(
  previous: FormalWorkspaceSessionInputs | null,
  next: FormalWorkspaceSessionInputs,
): boolean {
  return Boolean(
    previous &&
      previous.companyId === next.companyId &&
      previous.actorUserId === next.actorUserId &&
      previous.authorizationKey === next.authorizationKey &&
      previous.activeQuoteId === next.activeQuoteId &&
      previous.activeQuoteVersionId === next.activeQuoteVersionId &&
      previous.openQuoteIds.length === next.openQuoteIds.length &&
      previous.openQuoteIds.every(
        (id, index) =>
          id === next.openQuoteIds[index] &&
          previous.openQuotes[index] === next.openQuotes[index],
      ),
  );
}
type FormalWorkspaceSession = WorkspaceTabs &
  (
    | { schemaVersion: 2; scope: FormalWorkspaceSessionScope }
    | {
        schemaVersion: 3;
        scope: Omit<FormalWorkspaceSessionScope, 'authorizationKey'> & {
          authorizationFingerprint: string;
        };
      }
  );

const isFormalQuoteId = (id: string) =>
  /^[1-9][0-9]{0,18}$/.test(id) || isFormalNewQuoteId(id);

function parseSession(raw: string | null): FormalWorkspaceSession | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<FormalWorkspaceSession>;
    if (
      (value.schemaVersion !== 2 && value.schemaVersion !== 3) ||
      !value.scope ||
      typeof value.actorUserId !== 'string' ||
      typeof value.activeQuoteId !== 'string' ||
      (value.activeQuoteVersionId !== undefined &&
        typeof value.activeQuoteVersionId !== 'string') ||
      !Array.isArray(value.openQuoteIds) ||
      value.openQuoteIds.length > 10 ||
      !value.openQuoteIds.every(
        (id) => typeof id === 'string' && isFormalQuoteId(id),
      ) ||
      new Set(value.openQuoteIds).size !== value.openQuoteIds.length ||
      !value.quotes ||
      typeof value.quotes !== 'object' ||
      !value.openQuoteIds.every((id) => {
        const quote = value.quotes?.[id];
        return quote && quote.quoteId === id;
      }) ||
      (value.openQuoteIds.length > 0 &&
        !value.openQuoteIds.includes(value.activeQuoteId))
    )
      return null;
    return value as FormalWorkspaceSession;
  } catch {
    return null;
  }
}

/** New snapshots take precedence even when invalid; only an absent new key permits exact v2 recovery. */
export function readFormalWorkspaceSession(
  storage: StorageLike,
  scope: FormalWorkspaceSessionScope,
): WorkspaceTabs | null {
  const current = storage.getItem(formalWorkspaceSessionStorageKey(scope));
  const legacy = current === null;
  return parseFormalWorkspaceSnapshot(
    legacy
      ? storage.getItem(legacyFormalWorkspaceSessionStorageKey(scope))
      : current,
    scope,
    legacy,
  );
}

/** Strict authority validation shared by synchronous compatibility and durable ports. */
export function parseFormalWorkspaceSnapshot(
  raw: string | null,
  scope: FormalWorkspaceSessionScope,
  legacy = false,
): WorkspaceTabs | null {
  const session = parseSession(raw);
  if (
    !session ||
    session.actorUserId !== scope.actorUserId ||
    session.scope.companyId !== scope.companyId ||
    session.scope.actorUserId !== scope.actorUserId ||
    (legacy
      ? session.schemaVersion !== 2 ||
        session.scope.authorizationKey !== scope.authorizationKey
      : session.schemaVersion !== 3 ||
        session.scope.authorizationFingerprint !==
          authorizationFingerprint(scope))
  )
    return null;
  return session;
}

/** Restores only exact-authority browser tabs, never a quote master or server write. */
export function hydrateFormalWorkspaceSession(
  state: CpqState,
  storage: StorageLike,
  scope: FormalWorkspaceSessionScope,
): CpqState {
  let session: WorkspaceTabs | null;
  try {
    session = readFormalWorkspaceSession(storage, scope);
  } catch {
    return state;
  }
  return applyFormalWorkspaceTabs(state, session);
}

export function hydrateFormalWorkspaceSnapshot(
  state: CpqState,
  snapshot: string | null,
  scope: FormalWorkspaceSessionScope,
): CpqState {
  return applyFormalWorkspaceTabs(
    state,
    parseFormalWorkspaceSnapshot(snapshot, scope),
  );
}

function applyFormalWorkspaceTabs(
  state: CpqState,
  session: WorkspaceTabs | null,
): CpqState {
  if (!session) return state;
  if (session.openQuoteIds.length === 0) return state;
  const restoredQuotes = Object.fromEntries(
    Object.entries(session.quotes).map(([id, quote]) => [
      id,
      isFormalNewQuoteId(id)
        ? {
            ...quote,
            formalVersionId: undefined,
            formalVersionResourceVersion: undefined,
          }
        : quote,
    ]),
  );
  const quotes = { ...state.quotes, ...restoredQuotes };
  return {
    ...state,
    activeQuoteId: session.activeQuoteId,
    activeQuoteVersionId: bindKnownQuoteVersion(
      session.activeQuoteId,
      session.activeQuoteVersionId,
      quotes,
    ),
    openQuoteIds: session.openQuoteIds,
    quotes,
  };
}

export function serializeFormalWorkspaceSession(
  state: CpqState,
  scope: FormalWorkspaceSessionScope,
): string {
  const openQuoteIds = state.openQuoteIds.filter(isFormalQuoteId).slice(0, 10);
  const quotes = Object.fromEntries(
    openQuoteIds.flatMap((id) => {
      const quote = state.quotes[id];
      return quote ? [[id, quote] as const] : [];
    }),
  );
  const validOpenQuoteIds = openQuoteIds.filter((id) => Boolean(quotes[id]));
  const activeQuoteId = validOpenQuoteIds.includes(state.activeQuoteId)
    ? state.activeQuoteId
    : (validOpenQuoteIds.at(-1) ?? '');
  return serializeFormalWorkspaceTabs(
    {
      actorUserId: scope.actorUserId,
      activeQuoteId,
      ...(activeQuoteId === state.activeQuoteId && state.activeQuoteVersionId
        ? { activeQuoteVersionId: state.activeQuoteVersionId }
        : {}),
      openQuoteIds: validOpenQuoteIds,
      quotes,
    },
    scope,
  );
}

/** Includes non-open hidden case records when migrating an already validated snapshot. */
export function serializeFormalWorkspaceTabs(
  tabs: WorkspaceTabs,
  scope: FormalWorkspaceSessionScope,
): string {
  return JSON.stringify({
    ...tabs,
    quotes: Object.fromEntries(
      Object.entries(tabs.quotes).map(([id, quote]) => [
        id,
        isFormalNewQuoteId(id)
          ? {
              ...quote,
              formalVersionId: undefined,
              formalVersionResourceVersion: undefined,
              items: quote.items.map(
                ({ formalCustomFieldPreviews: _runtimePreviews, ...item }) =>
                  item,
              ),
            }
          : {
              ...quote,
              items: quote.items.map(
                ({ formalCustomFieldPreviews: _runtimePreviews, ...item }) =>
                  item,
              ),
            },
      ]),
    ),
    schemaVersion: 3,
    scope: {
      companyId: scope.companyId,
      actorUserId: scope.actorUserId,
      authorizationFingerprint: authorizationFingerprint(scope),
    },
    actorUserId: scope.actorUserId,
  } satisfies FormalWorkspaceSession);
}

export function persistFormalWorkspaceSession(
  state: CpqState,
  storage: StorageLike,
  scope: FormalWorkspaceSessionScope,
): void {
  storage.setItem(
    formalWorkspaceSessionStorageKey(scope),
    serializeFormalWorkspaceSession(state, scope),
  );
}
